import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { guard, confirmLeave, attachTracking } from "@/lib/unsavedGuard"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, dirty, onEscapeKeyDown, onPointerDownOutside, onInteractOutside, ...props }, ref) => {
  const id = React.useId()
  const inner = React.useRef(null)
  const closeRef = React.useRef(null)
  const bypass = React.useRef(false)
  const dirtyRef = React.useRef(dirty)
  dirtyRef.current = dirty
  const explicit = () => dirtyRef.current !== undefined
  const isDirty = () => !bypass.current && (explicit() ? !!dirtyRef.current : guard.is(id))
  const fwdRef = React.useRef(ref)
  fwdRef.current = ref
  // Le contenu n'est monté qu'à l'ouverture : on mémorise le nœud dans un state pour brancher le suivi à ce moment-là.
  const [node, setNode] = React.useState(null)
  const setRefs = React.useCallback((n) => {
    inner.current = n
    const r = fwdRef.current
    if (typeof r === "function") r(n); else if (r) r.current = n
    setNode(n)
  }, [])
  React.useEffect(() => {
    if (!node) return undefined
    return attachTracking(node, id, {
      explicit, isDirty,
      onLeave: (b) => { bypass.current = true; b.dataset.guardBypass = "1"; b.click(); delete b.dataset.guardBypass; bypass.current = false },
    })
  }, [node]) // eslint-disable-line
  React.useEffect(() => { if (dirty !== undefined) { if (dirty) guard.mark(id); else guard.clear(id) } }, [dirty, id])
  const askClose = () => confirmLeave().then((ok) => { if (ok) { guard.clear(id); bypass.current = true; closeRef.current?.click(); bypass.current = false } })
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      onEscapeKeyDown={(e) => { onEscapeKeyDown?.(e); if (!e.defaultPrevented && isDirty()) { e.preventDefault(); askClose() } }}
      onPointerDownOutside={(e) => { onPointerDownOutside?.(e); if (!e.defaultPrevented && isDirty()) { e.preventDefault(); askClose() } }}
      onInteractOutside={(e) => { onInteractOutside?.(e); if (!e.defaultPrevented && isDirty()) e.preventDefault() }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        ref={closeRef}
        data-guard-bypass="1"
        onClick={(e) => { if (isDirty()) { e.preventDefault(); askClose() } }}
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
